// src/app/models/sheet-descriptors.ts
import type { FilingFormState } from '../services/mbrs/filing-form-state.service';
import { SHEET_KEYS } from './sheet-keys';

/**
 * One row per sheet that participates in submit / retrieve / DTO mapping.
 * Aliases are kept as SEPARATE fields on purpose — `submitSlug` (PascalCase,
 * the SheetStatuses/SheetCounts key) and `retrieveSlug` (kebab, the
 * retrieve-mbrs-data key) are DIFFERENT namespaces and must not be merged.
 *
 * `schemaCommon` is set for taxonomy-agnostic sheets (one JSON file);
 * `schemaMfrs`/`schemaMpers` are set for sheets with split schemas. Values are
 * basenames under `/assets/forms/` WITHOUT the `.form.json` suffix.
 *
 * Single source of truth: the three legacy maps
 *   - STATE_KEY_TO_SLUG (submit-for-validation.component.ts)
 *   - sheetToDtoMap     (form-validation-submit.service.ts)
 *   - SLUG_TO_STATE_KEY (conversion-validator.service.ts)
 * are derived from this table (see helpers below). `sheet-descriptors.spec.ts`
 * pins the derivations 1:1 against the original literals.
 */
export interface SheetDescriptor {
  stateKey: keyof FilingFormState;
  submitSlug?: string;
  retrieveSlug?: string;
  commonDto?: string;
  mfrsDto?: string;
  mpersDto?: string;
  schemaCommon?: string;
  schemaMfrs?: string;
  schemaMpers?: string;
}

export const SHEET_DESCRIPTORS: readonly SheetDescriptor[] = [
  {
    stateKey: SHEET_KEYS.filingPrerequisites,
    submitSlug: 'FilingInfo',
    commonDto: 'FilingPrerequisitesDto',
    schemaCommon: 'role-0',
  },
  {
    stateKey: SHEET_KEYS.filingInfo,
    submitSlug: 'FilingInfo',
    retrieveSlug: 'filing-info',
    commonDto: 'FilingInfoDto',
    schemaCommon: 'role-1',
  },
  {
    stateKey: SHEET_KEYS.scopeOfFiling,
    submitSlug: 'ScopeOfFiling',
    retrieveSlug: 'scope-of-filing',
    mfrsDto: 'MfrsScopeOfFilingDto',
    mpersDto: 'MpersScopeOfFilingDto',
    schemaCommon: 'role-2',
  },
  {
    stateKey: SHEET_KEYS.stockExchangeDisclosure,
    submitSlug: 'InvolveInSE',
    retrieveSlug: 'stock-exchange',
    mfrsDto: 'MfrsInvolveInSEDto',
    mpersDto: 'MpersInvolveInSEDto',
    schemaCommon: 'role-3',
  },
  {
    stateKey: SHEET_KEYS.directorsReport,
    submitSlug: 'DirectorsRep',
    retrieveSlug: 'directors-report',
    mfrsDto: 'MfrsDirectorsRepDto',
    mpersDto: 'MpersDirectorsRepDto',
    schemaCommon: 'role-120000',
  },
  {
    stateKey: SHEET_KEYS.statementByDirectors,
    submitSlug: 'StatOfDirectors',
    retrieveSlug: 'statement-by-directors',
    mfrsDto: 'MfrsStatOfDirectorsDto',
    mpersDto: 'MpersStatOfDirectorsDto',
    schemaCommon: 'role-120100',
  },
  {
    stateKey: SHEET_KEYS.directorBusinessReview,
    submitSlug: 'DirectorsBussRev',
    retrieveSlug: 'director-business-review',
    mfrsDto: 'MfrsDirectorsBussRevDto',
    mpersDto: 'MpersDirectorsBussRevDto',
    schemaCommon: 'role-120200',
  },
  {
    stateKey: SHEET_KEYS.auditorsReport,
    submitSlug: 'AuditReport',
    retrieveSlug: 'auditors-report',
    mfrsDto: 'MfrsAuditReportDto',
    mpersDto: 'MpersAuditReportDto',
    schemaCommon: 'role-130000',
  },
  {
    stateKey: SHEET_KEYS.statementOfFinancialPosition,
    submitSlug: 'SofpCuNonCu',
    retrieveSlug: 'statement-of-financial-position',
    mfrsDto: 'MfrsSofpCuNonCuDto',
    mpersDto: 'MpersSofpCuNonCuDto',
    schemaMfrs: 'role-210000-mfrs',
    schemaMpers: 'role-210000-mpers',
  },
  {
    stateKey: SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
    submitSlug: 'SofpSubCuNonCu',
    retrieveSlug: 'sub-classification',
    mfrsDto: 'MfrsSofpSubCuNonCuDto',
    mpersDto: 'MpersSofpSubCuNonCuDto',
    schemaMfrs: 'role-210100-mfrs',
    schemaMpers: 'role-210100-mpers',
  },
  {
    stateKey: SHEET_KEYS.statementOfProfitOrLoss,
    submitSlug: 'SoplFunction',
    retrieveSlug: 'statement-of-profit-or-loss',
    mfrsDto: 'MfrsSoplFunctionDto',
    mpersDto: 'MpersSoplFunctionDto',
    schemaMfrs: 'role-310000-mfrs',
    schemaMpers: 'role-310000-mpers',
  },
  {
    stateKey: SHEET_KEYS.analysisOfProfitOrLoss,
    submitSlug: 'SoplAnalysisFunction',
    retrieveSlug: 'analysis-of-profit-or-loss',
    mfrsDto: 'MfrsSoplAnalysisFunctionDto',
    mpersDto: 'MpersSoplAnalysisFunctionDto',
    schemaMfrs: 'role-310100-mfrs',
    schemaMpers: 'role-310100-mpers',
  },
  {
    stateKey: SHEET_KEYS.statementOfComprehensiveIncome,
    submitSlug: 'SociNetOfTax',
    retrieveSlug: 'statement-of-comprehensive-income',
    mfrsDto: 'MfrsSociNetOfTaxDto',
    mpersDto: 'MpersSociNetOfTaxDto',
    schemaMfrs: 'role-410000-mfrs',
    schemaMpers: 'role-410000-mpers',
  },
  {
    stateKey: SHEET_KEYS.statementOfCashFlowsIndirectMethod,
    submitSlug: 'SocfIndirect',
    retrieveSlug: 'statement-of-cash-flows-indirect',
    mfrsDto: 'MfrsSocfIndirectDto',
    mpersDto: 'MpersSocfIndirectDto',
    schemaMfrs: 'role-520000-mfrs',
    schemaMpers: 'role-520000-mpers',
  },
  {
    stateKey: SHEET_KEYS.statementOfChangesInEquity,
    submitSlug: 'Socie',
    retrieveSlug: 'statement-of-changes-in-equity',
    mfrsDto: 'MfrsSocieDto',
    mpersDto: 'MpersSocieDto',
    schemaMfrs: 'role-610000-mfrs',
    schemaMpers: 'role-610000-mpers',
  },
  {
    stateKey: SHEET_KEYS.notesCorporateInformation,
    submitSlug: 'NotesCi',
    retrieveSlug: 'notes-corporate-information',
    mfrsDto: 'MfrsNotesCiDto',
    mpersDto: 'MpersNotesCiDto',
    schemaMfrs: 'role-710000-mfrs',
    schemaMpers: 'role-710000-mpers',
  },
  {
    stateKey: SHEET_KEYS.notesAccountingPolicies,
    submitSlug: 'NotesSummaryofAccPol',
    retrieveSlug: 'notes-accounting-policies',
    mfrsDto: 'MfrsNotesSummaryofAccPolDto',
    mpersDto: 'MpersNotesSummaryofAccPolDto',
    schemaMfrs: 'role-720000-mfrs',
    schemaMpers: 'role-720000-mpers',
  },
  {
    stateKey: SHEET_KEYS.notesListOfNotes,
    submitSlug: 'NotesListofnotes',
    retrieveSlug: 'notes-list-of-notes',
    mfrsDto: 'MfrsNotesListofnotesDto',
    mpersDto: 'MpersNotesListofnotesDto',
    schemaMfrs: 'role-730000-mfrs',
    schemaMpers: 'role-730000-mpers',
  },
  {
    stateKey: SHEET_KEYS.notesIssuedCapital,
    submitSlug: 'NotesIssuedcapital',
    retrieveSlug: 'notes-issued-capital',
    mfrsDto: 'MfrsNotesIssuedcapitalDto',
    mpersDto: 'MpersNotesIssuedcapitalDto',
    schemaMfrs: 'role-740000-mfrs',
    schemaMpers: 'role-740000-mpers',
  },
  {
    stateKey: SHEET_KEYS.notesRelatedPartyTransactions,
    submitSlug: 'NotesRelatedPartytran',
    retrieveSlug: 'notes-related-party-transactions',
    mfrsDto: 'MfrsNotesRelatedPartytranDto',
    mpersDto: 'MpersNotesRelatedPartytranDto',
    schemaMfrs: 'role-750000-mfrs',
    schemaMpers: 'role-750000-mpers',
  },
];

/** Fast lookup by stateKey. */
export const SHEET_DESCRIPTOR_BY_STATE_KEY: ReadonlyMap<string, SheetDescriptor> =
  new Map(SHEET_DESCRIPTORS.map((descriptor) => [descriptor.stateKey, descriptor]));

/**
 * Derive STATE_KEY_TO_SLUG (submit-for-validation.component.ts): stateKey ->
 * submit slug, for sheets that carry a submitSlug. `filingPrerequisites` shares
 * the `FilingInfo` slug but is excluded from the submit map (the original map
 * keyed only the 19 submittable sheets, with `filingInfo` as the FilingInfo
 * entry), so we drop it here to stay 1:1.
 */
export function buildStateKeyToSlug(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const descriptor of SHEET_DESCRIPTORS) {
    if (descriptor.submitSlug && descriptor.stateKey !== SHEET_KEYS.filingPrerequisites) {
      out[descriptor.stateKey] = descriptor.submitSlug;
    }
  }
  return out;
}

/** Derive the sheetToDtoMap (form-validation-submit.service.ts). */
export function buildSheetToDtoMap(): Record<
  string,
  { mfrsDto?: string; mpersDto?: string; commonDto?: string }
> {
  const out: Record<string, { mfrsDto?: string; mpersDto?: string; commonDto?: string }> = {};
  for (const descriptor of SHEET_DESCRIPTORS) {
    if (!descriptor.commonDto && !descriptor.mfrsDto && !descriptor.mpersDto) continue;
    const entry: { mfrsDto?: string; mpersDto?: string; commonDto?: string } = {};
    if (descriptor.commonDto) entry.commonDto = descriptor.commonDto;
    if (descriptor.mfrsDto) entry.mfrsDto = descriptor.mfrsDto;
    if (descriptor.mpersDto) entry.mpersDto = descriptor.mpersDto;
    out[descriptor.stateKey] = entry;
  }
  return out;
}

/** Derive SLUG_TO_STATE_KEY (conversion-validator.service.ts): retrieve slug -> stateKey. */
export function buildSlugToStateKey(): Record<string, keyof FilingFormState> {
  const out: Record<string, keyof FilingFormState> = {};
  for (const descriptor of SHEET_DESCRIPTORS) {
    if (descriptor.retrieveSlug) out[descriptor.retrieveSlug] = descriptor.stateKey;
  }
  return out;
}

/**
 * Derive the inbound submit-slug -> stateKey map used by the container's
 * `applyApiSheetStatuses()` (which applies the API's PascalCase SheetStatuses
 * keys to local state). This is the inverse of {@link buildStateKeyToSlug} and
 * therefore shares its `filingPrerequisites` exclusion — the `FilingInfo` slug
 * resolves to `filingInfo` (the submittable sheet), never to the Phase-1
 * prerequisites slot. Replaces a hand-maintained literal that could drift from
 * this table; pinned 1:1 in `sheet-descriptors.spec.ts`.
 */
export function buildSubmitSlugToStateKey(): Record<string, keyof FilingFormState> {
  const out: Record<string, keyof FilingFormState> = {};
  for (const [stateKey, slug] of Object.entries(buildStateKeyToSlug())) {
    out[slug] = stateKey as keyof FilingFormState;
  }
  return out;
}

/**
 * Derive role-code -> stateKey, where the role code is the 6-digit code embedded
 * in a sheet's schema basename (e.g. `role-210100-mfrs` -> `210100`). Used to
 * resolve a control's `ui.linkTo.role` to the state key whose N8N snapshot holds
 * the inherited (linkTo) field's authoritative extracted value — the link
 * target. Sheets whose basename carries no 6-digit code (role-0/1/2) are not
 * link targets and are omitted. First basename to declare a code wins (the
 * MFRS/MPERS split share one code, so order is irrelevant).
 */
export function buildRoleCodeToStateKey(): Record<string, keyof FilingFormState> {
  const out: Record<string, keyof FilingFormState> = {};
  for (const descriptor of SHEET_DESCRIPTORS) {
    for (const basename of [descriptor.schemaCommon, descriptor.schemaMfrs, descriptor.schemaMpers]) {
      const code = basename?.match(/(\d{6})/)?.[1];
      if (code && !(code in out)) out[code] = descriptor.stateKey;
    }
  }
  return out;
}

/** Resolve the schema basename for a sheet under a taxonomy (null if none). */
export function resolveSchemaBasename(
  descriptor: SheetDescriptor,
  taxonomy: 'MFRS' | 'MPERS'
): string | null {
  if (descriptor.schemaCommon) return descriptor.schemaCommon;
  return taxonomy === 'MFRS'
    ? descriptor.schemaMfrs ?? null
    : descriptor.schemaMpers ?? null;
}
